import { useEffect, useState } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { reportError } from "../../../../../shared/errorReporting";
import { PageHead, PageShell } from "../../components/PageShell";
import { useTranslation } from "../../i18n/useTranslation";
import { isModuleLoadFailure } from "../../../../../shared/lib/moduleLoadFailure";
import { chunkFailureVerdict, type ChunkFailureVerdict } from "../../features/release/staleBuild";

// Shown when a route throws — most realistically a failed chunk or asset fetch
// on a mobile network, which is the normal case for this surface: a phone
// following a QR code, on whatever signal it has.
//
// Without this, React Router renders its built-in "Unexpected Application
// Error!" screen, stack trace and all, to a client trying to book a haircut.
//
// No Footer (`legal={false}`): the footer needs the translation provider, and
// if THAT is what failed this component still has to render.
/** The current path as a link that cannot leave the origin. */
export function sameOriginPath(pathname: string): string {
  return "/" + pathname.replace(/^[\/\\]+/, "");
}

function RouteError() {
  const { t } = useTranslation();
  const error = useRouteError();

  // A CHUNK THAT FAILED TO LOAD ASKS BUILD-INFO (rc.12 (a), the Web
  // Director's amendment of rc.9's rule). A newer release live: the page's one
  // release notice (PageShell) offers the reload, and nothing else does.
  // Current, unreachable or offline: not a new version, so the honest generic
  // failure, whose link reloads the same address. Until the answer (at most
  // 1.5 s) neither shows, so the page never offers both.
  const chunkFailed = isModuleLoadFailure(error);
  const [verdict, setVerdict] = useState<ChunkFailureVerdict | null>(null);

  // Reported through the allowlisted, scrubbed path in shared/errorReporting.
  // A router 404 is a wrong URL, not a fault, and is not reported.
  useEffect(() => {
    if (!isRouteErrorResponse(error)) reportError(error);
  }, [error]);
  useEffect(() => {
    if (!chunkFailed) return;
    let live = true;
    void chunkFailureVerdict().then((answer) => {
      if (live) setVerdict(answer);
    });
    return () => {
      live = false;
    };
    // Keyed on the error itself: a different failure asks again.
  }, [chunkFailed, error]);
  const newer = chunkFailed && verdict === "newer";
  const offerRetry = !chunkFailed || (verdict !== null && verdict !== "newer");

  return (
    <PageShell legal={false} testId="route-error" state={newer ? "chunk-gone" : undefined}>
      <PageHead title={t("routeError.title")} sub={t("routeError.body")} />
      {offerRetry ? (
        <p className="b2-hint">
          {/* Same-origin by construction: a pathname such as "//evil.example/x"
              would otherwise be a protocol-relative link off the origin. The
              query is kept: it carries the flow's selection. */}
          <a href={typeof window === "undefined" ? "/" : sameOriginPath(window.location.pathname) + window.location.search} data-testid="route-error-retry">
            {t("routeError.retry")}
          </a>
        </p>
      ) : null}
    </PageShell>
  );
}

export default RouteError;
